
import React from "react";
import CompanyLayout from "@/components/CompanyLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowRight, Shield, Lock, Database, Server, FileCheck, 
  CheckCircle, FileText, Globe, Building, Users
} from "lucide-react";

const Security = () => {
  const pageTitle = "Security & Compliance";
  const pageDescription = "Learn about Assembly's robust security protocols and compliance certifications.";

  const securityFeatures = [
    {
      icon: <Shield className="h-10 w-10 text-assembly-blue" />,
      title: "SOC 2 Type II Certified",
      description: "We undergo regular SOC 2 Type II audits to ensure the highest standards of security and compliance."
    },
    {
      icon: <Lock className="h-10 w-10 text-assembly-purple" />,
      title: "Advanced Encryption",
      description: "All data is encrypted at rest and in transit using industry-standard encryption protocols."
    },
    {
      icon: <CheckCircle className="h-10 w-10 text-assembly-green" />,
      title: "GDPR Compliant",
      description: "Our platform is designed with privacy by default and adheres to GDPR requirements."
    },
    {
      icon: <FileCheck className="h-10 w-10 text-assembly-orange" />,
      title: "CCPA Compliance",
      description: "We comply with the California Consumer Privacy Act (CCPA) for California residents."
    },
    {
      icon: <Database className="h-10 w-10 text-assembly-pink" />,
      title: "Secure Data Storage",
      description: "Data stored in highly secure, redundant environments with strict access controls."
    },
    {
      icon: <Server className="h-10 w-10 text-assembly-blue" />,
      title: "Regular Penetration Testing",
      description: "Independent security experts regularly test our infrastructure for vulnerabilities."
    }
  ];

  const dataProtectionPractices = [
    "Role-based access controls limit data access to authorized personnel",
    "Regular security assessments and vulnerability scanning",
    "Comprehensive incident response plan and procedures",
    "Employee security awareness training and best practices",
    "Secure development lifecycle for all software updates",
    "24/7 infrastructure monitoring and threat detection"
  ];

  return (
    <CompanyLayout title={pageTitle} description={pageDescription}>
      {/* Hero Section */}
      <section className="bg-hero-gradient py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">Security You Can Trust</h1>
            <p className="text-xl md:text-2xl text-gray-700 mb-8">
              Your data security and privacy are our top priorities. Assembly employs industry-leading security practices to protect your information.
            </p>
            <Button 
              className="bg-assembly-orange hover:bg-orange-600 text-white py-6 px-10 rounded-xl text-lg font-medium transition-all hover:shadow-lg flex items-center gap-2 mx-auto"
              onClick={() => {
                const securityFeatures = document.getElementById('security-features');
                if (securityFeatures) securityFeatures.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Learn More About Our Security
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Security Badge Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto p-8 border border-gray-100 rounded-2xl shadow-lg flex flex-col md:flex-row items-center justify-between">
            <div className="md:w-3/5 mb-8 md:mb-0 md:pr-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">SOC 2 Type II Compliant</h2>
              <p className="text-gray-700">
                Assembly has successfully completed a SOC 2 Type II audit, verifying that our security controls and practices meet the highest industry standards for securing customer data.
              </p>
            </div>
            <div className="md:w-2/5 flex justify-center">
              <div className="w-40 h-40 flex items-center justify-center bg-gray-50 rounded-full p-4 shadow-inner">
                <Shield className="h-20 w-20 text-assembly-blue" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section id="security-features" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Security & Compliance Program</h2>
            <p className="text-lg text-gray-700">
              Assembly employs comprehensive security measures to ensure your data remains protected.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {securityFeatures.map((feature, index) => (
              <Card key={index} className="border-none shadow-lg hover-card-effect">
                <CardContent className="p-8">
                  <div className="mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-700">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Data Protection Practices */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Data Protection Practices</h2>
              <p className="text-lg text-gray-700 mb-6">
                We implement multiple layers of security to safeguard your data at every level.
              </p>
              <ul className="space-y-4">
                {dataProtectionPractices.map((practice, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-assembly-green mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{practice}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-blue-gradient rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Infrastructure</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="mr-4 mt-1">
                    <Server className="h-6 w-6 text-assembly-blue" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Cloud Infrastructure</h4>
                    <p className="text-gray-700">Hosted on industry-leading cloud providers with built-in redundancy and disaster recovery capabilities.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="mr-4 mt-1">
                    <Lock className="h-6 w-6 text-assembly-blue" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Security Testing</h4>
                    <p className="text-gray-700">Regular vulnerability assessments and penetration testing by third-party security experts.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="mr-4 mt-1">
                    <Database className="h-6 w-6 text-assembly-blue" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Data Backups</h4>
                    <p className="text-gray-700">Automated, encrypted backups with rigorous restoration testing procedures.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Compliance Certifications</h2>
            <p className="text-lg text-gray-700">
              Assembly adheres to global privacy regulations and industry standards.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-none shadow-lg hover-card-effect">
              <CardContent className="p-8">
                <div className="mb-4">
                  <FileText className="h-10 w-10 text-assembly-blue" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">GDPR Compliance</h3>
                <p className="text-gray-700 mb-4">
                  Assembly is fully compliant with the EU's General Data Protection Regulation, ensuring the proper handling of personal data for EU residents.
                </p>
                <Button 
                  variant="outline"
                  className="w-full justify-between"
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window.open("/gdpr-policy", "_blank");
                    }
                  }}
                >
                  View Our GDPR Policy
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover-card-effect">
              <CardContent className="p-8">
                <div className="mb-4">
                  <FileText className="h-10 w-10 text-assembly-orange" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">CCPA Compliance</h3>
                <p className="text-gray-700 mb-4">
                  Assembly complies with the California Consumer Privacy Act, protecting the privacy rights of California residents.
                </p>
                <Button 
                  variant="outline"
                  className="w-full justify-between"
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window.open("/ccpa-policy", "_blank");
                    }
                  }}
                >
                  View Our CCPA Policy
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Security FAQ</h2>
            <p className="text-lg text-gray-700">
              Common questions about Assembly's security and privacy practices.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">How is my data protected?</h3>
              <p className="text-gray-700">
                Assembly encrypts all data at rest and in transit. We implement strict access controls, regular security audits, and follow security best practices to ensure your data remains protected.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Where is my data stored?</h3>
              <p className="text-gray-700">
                All customer data is stored in secure, SOC 2 compliant data centers. For customers with specific data residency requirements, we offer regional data storage options.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">How do you handle security incidents?</h3>
              <p className="text-gray-700">
                Assembly has a comprehensive incident response plan that includes immediate containment, thorough investigation, appropriate remediation, and timely notification to affected customers as required by law.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-orange-gradient rounded-3xl mx-6 my-12">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Have questions about our security?</h2>
          <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            Our team is happy to provide more information about our security practices and answer any questions you may have.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              className="bg-assembly-blue hover:bg-blue-600 text-white py-6 px-10 rounded-xl text-lg font-medium transition-all hover:shadow-lg flex items-center gap-2 justify-center"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.open("https://trust.joinassembly.com", "_blank");
                }
              }}
            >
              Visit Our Trust Center
              <ArrowRight className="h-5 w-5" />
            </Button>
            <Button 
              variant="outline"
              className="bg-white hover:bg-gray-50 py-6 px-10 rounded-xl text-lg font-medium transition-all hover:shadow-lg flex items-center gap-2 justify-center"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.location.href = '/company/contact';
                }
              }}
            >
              Contact Our Security Team
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>
    </CompanyLayout>
  );
};

export default Security;
